import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { LiquidityCapTableByShareClassStakeholder } from '../../../../../types/scenario-modeling.types';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { CapTableTab, Column, ShareClassColumns } from '../liquidity.types';
import { TableRow } from '../table-components/index';

export type ShareClassTableItemProps = {
  className?: string;
  fields: Record<
    ShareClassColumns,
    { change: null | null | number; value: number | number[] | string | string[] } | null
  >;
  isModified?: boolean;
  items?: LiquidityCapTableByShareClassStakeholder[];
  visibleColumns: Column[];
};

export const ShareClassTableItem: FC<ShareClassTableItemProps> = ({
  fields,
  visibleColumns,
  className,
  items = [],
  isModified = false,
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const isItemsEmpty = items.length === 0;

  return (
    <>
      <TableRow
        fields={fields}
        handleExpandRow={setExpanded}
        isExpanded={isExpanded}
        isExpandedRow={isItemsEmpty}
        tab={CapTableTab.BY_SHARE_CLASS}
        tdClassName={className}
        trClassName={twMerge(isExpanded && 'bg-brand-25')}
        visibleColumns={visibleColumns}
      />
      {isExpanded && (
        <>
          {items.map(
            (
              {
                avgSharePrice,
                diluted,
                dilutedPercentage,
                investment,
                issued,
                issuedPercentage,
                votingPercentage,
                name,
                voting,
                capParticipating,
                commonStockProceeds,
                effectiveLiquidityProceeds,
                investmentDays,
                minLiquidityProceeds,
                multiple,
                participatingOnProceeds,
                proceedsWithoutLiquidity,
                roi,
                seniorityColumns,
              },
              i,
            ) => {
              return (
                <TableRow
                  className="bg-[#F9FAFB] shadow-[inset_2px_0px_0px_0px_#2565C8]"
                  fields={{
                    [Column.INVESTMENT_TIME]: { change: null, value: investmentDays },
                    [Column.MULTIPLE]: { change: null, value: multiple },
                    [Column.CAP_PARTICIPATING]: { change: null, value: capParticipating },
                    [Column.NAME]: { value: name, change: null },
                    [Column.ISSUED]: {
                      change: null,
                      value: issuedPercentage * 100,
                    },
                    [Column.DILUTED]: {
                      value: dilutedPercentage * 100,
                      change: null,
                    },
                    [Column.VOTING]: {
                      value: votingPercentage * 100,
                      change: null,
                    },

                    [Column.NO_ISSUED]: {
                      change: null,
                      value: issued,
                    },
                    [Column.DILUTED_SHARES]: {
                      change: null,
                      value: diluted,
                    },

                    [Column.VOTING_RIGHTS]: {
                      change: null,
                      value: voting,
                    },
                    [Column.INVESTMENT]: {
                      change: null,
                      value: investment,
                    },
                    [Column.AVERAGE_SHARE_PRICE]: {
                      change: null,
                      value: avgSharePrice,
                    },

                    [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
                      change: null,
                      value: proceedsWithoutLiquidity,
                    },
                    [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: minLiquidityProceeds },
                    [Column.PARTICIPATING_ON_PROCEEDS]: {
                      change: null,
                      value: toMathRound(participatingOnProceeds, 0),
                    },
                    [Column.COMMON_STOCK_PROCEEDS]: { value: commonStockProceeds, change: null },
                    [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
                      value: effectiveLiquidityProceeds,
                      change: null,
                    },
                    [Column.ROI]: { change: null, value: roi },
                    [Column.SENIORITY]: {
                      change: null,
                      value: seniorityColumns?.map(({ seniorityValue }) => seniorityValue),
                    },
                  }}
                  isExpandedRow={true}
                  key={`${i}_${name}`}
                  showToolTip={isModified}
                  tab={CapTableTab.BY_SHARE_CLASS}
                  trClassName="bg-brand-25"
                  visibleColumns={visibleColumns}
                />
              );
            },
          )}
        </>
      )}
    </>
  );
};
